




















































































































import {Component, Vue, Prop, Watch, Ref, Provide} from 'vue-property-decorator'
import {mixins} from 'vue-class-component'
import * as NGLViewer from 'ngl'
import * as Three from 'three'
import {RepresentationSet} from '@/models'
import {Axis3DHelper} from '@/axis-helper'
import {ChoiceParamDef, Param} from "@/params"
import {ParamStore} from '@/store/paramstore'
import {SceneShot, CameraParams, CameraZoomParams} from '@/store/shots'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import {download} from '@/storage'
import {userStore, projectStore, shotStore, templateStore, cameraTrackStore, nglStore} from '@/store'
import {forEachEntry} from '@/map-utils'
import {Keyframe, KeyframeList} from '@/keyframe'
import {readCameraFromScene, getCameraPose} from '@/camera'
import {CameraRig, CameraRigFree} from '@/camera-rig'
import {FreeCameraMouseControls, MouseControls} from '@/mouse-controls'
import {getComponentRawCenter,
        componentVisualCenterToOrigin,
        getComponentVisualBbox,
        compTransformToString} from '@/ngl-component-utils'
import {v3ToString, scaleToString, matrix4ToString, v4ToString} from '@/three-utils'
import {fuzzyEqual} from '@/utils'
import {CameraTrack} from '@/store/camera-tracks'
import {PostprocessEffects} from '@/effects/postprocess'
import {SceneTemplate} from '@/store/templates'
import {renderResolutions} from '@/resolutions'
import type {RenderResolution} from '@/resolutions'
import {BaseError, fullStack} from 'make-error-cause'
import {inspect} from 'util'
import GlobalEvents from 'vue-global-events'
import {undoStack} from '@/store/undo-plugin'

// sub-components
import {nglContext, NGLContext} from '@/components/NGL/nglContext'
import NumberInput from '@/components/NumberInput.vue'
import NGLParamsDrawer from '@/components/NGL/NGLParamsDrawer.vue'
import NGLMouseHints from '@/components/NGLMouseHints.vue'
import ParamsMixin from '@/components/NGL/ParamsMixin'
import UtilsMixin from '@/components/NGL/UtilsMixin'
import SceneImportMixin from '@/components/NGL/SceneImportMixin'
import SceneExportMixin from '@/components/NGL/SceneExportMixin'
import NglLeftSidebar from '@/components/NGL/NglLeftSidebar.vue'
import ParamsUtils from '@/components/NGL/ParamsUtils'
import {maybeStartTour, tourShownStatus} from '@/tour'

class LoadError extends BaseError {}

const tmpQuat = new Three.Quaternion()
const tmpV3 = new Three.Vector3()
const tmpM = new Three.Matrix4()

// This shows the whole error chain with stacks for each one
function errorFullStack(error: Error | BaseError) {
  const SEPARATOR_TEXT = '\n--- which was caused by the following: ---\n'
  const chain: Error[] = [];
  let cause: Error | undefined = error;

  while (cause) {
    chain.push(cause);
    cause = (cause as BaseError).cause;
  }

  return chain
    .map(err => err.stack)
    .join(SEPARATOR_TEXT);
}
function logError(error: Error | BaseError) {
  console.error(errorFullStack(error))
}

// Use this to debug Firestore issues:
// Firebase.firestore.setLogLevel('debug')

type Unwatch = () => void;


// A transform matrix decomposed into position, scale, and rotation.
type Transform = {
  position: Three.Vector3;
  scale: Three.Vector3;
  rotation: Three.Quaternion;
}

type ComponentTransforms = { [compId: string]: Transform };
type ComponentReprs = { [compId: string]: RepresentationSet };

type TickArgs = typeof NGLViewer.Stage.prototype.viewer.stats

// Clip/fog units are in Angstrom; typical virus is ~500A diameter
export const NGLStageDefaultParams: Partial<NGLViewer.StageParameters> = {
  impostor: true,
  clipMode: 'camera',
  clipScale: 'absolute',
  clipNear: 0.01,
  clipFar: 100000,
  fogNear: 0.01,
  fogFar: 100000,
  lightIntensity: 0,
  ambientIntensity: 0,
  rotateSpeed: 0.5,             // default is 2; too fast
}

// This is called as a signal handler when a component's selection changes.
// Is gets called with `this` bound to the component.
// (note the fake Typescript 'this' param.)
function onCompSelectionChanged(this: NGLViewer.StructureComponent, sele: string) {
  console.log(`Component ${this.uuid}: selection changed to ${sele}`)

  const oldMatrix = this.matrix.clone()
  console.log(`Old matrix:\n${matrix4ToString(oldMatrix)}`)

  this.updateMatrix(true) // get matrix w/ new selection-center (silently, no signals)
  console.log(`New matrix:\n${matrix4ToString(this.matrix)}`)

  // Update pre-transform to make final result same as m0 (old matrix)
  // T' = m0 * m1^-1 * T
  tmpM.getInverse(this.matrix)
  this.transform.premultiply(tmpM).premultiply(oldMatrix)
  console.log(`Transform:\n${matrix4ToString(this.transform)}`)
  this.updateMatrix()
}

@Component({
  components: {
    NGLParamsDrawer,
    VuePerfectScrollbar, NGLMouseHints, NumberInput,
    NglLeftSidebar, GlobalEvents,
  }
})
export default class NGL extends mixins<Vue & ParamsMixin & UtilsMixin &
SceneImportMixin & SceneExportMixin>(ParamsMixin, UtilsMixin, SceneImportMixin, SceneExportMixin) {
  @Prop() shot!: SceneShot
  @Prop() expanded!: boolean
  @Ref() viewport!: any
  @Ref() nglParams!: NGLParamsDrawer
  @Ref() nglLeftSidebar!: any
  @Provide('NGLMainStage')
  nglMainStage() {
    return nglContext.get(this)?.stage
  }

  context() {
    return nglContext.get(this)
  }

  stageParams: Partial<NGLViewer.StageParameters> = Object.assign({}, NGLStageDefaultParams);
  cameraRig?: CameraRig
  cameraRigFree?: CameraRigFree
  freeCameraMouseControls?: FreeCameraMouseControls
  mouseControls?: MouseControls

  nglMouseControls() {
    return this.mouseControls ? this.mouseControls : null
  }

  spinning = false
  message = 'OK'
  showBgLoadingProgress = false
  showBgTaskProgressIndicator = false
  showBgTaskProgressText = ""
  // Turn this on to debug molecule autoscaling:
  moleculeAutoScaleVerbose = false
  defaultRenderResolution: RenderResolution = 'Standard HD (720p)'
  renderResolutions = renderResolutions()
  loadingComponents = false

  progressPercent = 0
  shouldLoadBackgroundScene = true
  showDebugOverlay = false
  updateDebugText = 0

  sceneUrl: string|null = null // set when model has been saved; cleared on modification
  rotateScene?: (dx: number, dy: number) => void

  representationParamStore: { [reprId: string]: ParamStore } = {} // all representations for all components, flattened (so just by reprId)
  representationWatchers: Unwatch[] = []
  postprocessEffects?: PostprocessEffects

  get nFrames() {
    return this.shot.durationInSec * (projectStore?.project?.projectFPS || 24)
  }

  get debugText() {
    this.updateDebugText        // just access it to establish dependency
    const comp = this.shot.components[0]
    if (!comp)
      return '-no comp-'
    const nglComp = this.getNGLComponent(comp.id)
    if (!nglComp) {
      return `Comp 0 of ${this.shot.components.length}:\n` +
          `${comp.name} ${comp.id.slice(0, 7)}: no NGL comp`
    }

    const compInfo = compTransformToString(comp, nglComp)
    const sceneTransformInfo = this.sceneTransformToString()
    const compMatrix = matrix4ToString(nglComp.matrix)
    return `Comp 0 of ${this.shot.components.length}:\n${compInfo}\nScene transform:\n${sceneTransformInfo}\n` +
        `Comp Matrix:\n${compMatrix}\n` +
        `center: {${v3ToString(nglComp.getCenterUntransformed())}}` +
        `, position: {${v3ToString(nglComp.position)}}`
  }

  async mounted() {
    console.log(`Starting NGL`)

    const motionNotificationCallback = () => {
      (this.$refs.mouseHints as any).notifyMotion()
    }
    this.cameraRig = new CameraRig()
    this.cameraRigFree = new CameraRigFree()
    this.freeCameraMouseControls = new FreeCameraMouseControls(motionNotificationCallback)
    // Create the stage and viewer
    // NGLViewer.setDebug(true)    // enable for testing
    const stage = new NGLViewer.Stage(this.$refs.viewport as any, this.stageParams)
    this.mouseControls = new MouseControls(stage, this.freeCameraMouseControls, this.selectComponent)
    // Set up linear color workflow: translate input colors to linear,
    // and translate output colors back to sRGB.
    stage.viewer.setColorWorkflow('linear')

    this.postprocessEffects = new PostprocessEffects(stage)

    // add background groups where we'll import the scaled and unscaled bg scene
    const backgroundGroup = new Three.Group()
    backgroundGroup.name = 'bg scene'
    stage.viewer.scene.add(backgroundGroup)

    const scaledBackgroundGroup = new Three.Group()
    scaledBackgroundGroup.name = 'scaled bg scene'
    scaledBackgroundGroup.position.set(0, -1, 0)
    stage.viewer.scene.add(scaledBackgroundGroup)


    const context: NGLContext = {stage,
                                 viewer: stage.viewer,
                                 scene: stage.viewer.scene,
                                 renderer: stage.viewer.renderer}
    // Store the stage and associated three.js objects in our context.
    // We do this so those objects don't become reactive, as they would if we made them
    // member variables of the NGL component class.
    nglContext.set(this, context)

    if (this.followCameraTrackMode)
      this.cameraRig.setup(context.viewer)
    else
      this.cameraRigFree.setup(context.viewer)

    // For Three.js Inspector:
    // window.scene = viewer.scene

    // enable shadows for this renderer
    // DO NOT ENABLE -- causes shader program failures w/ three r111
    const enableShadows = false
    if (enableShadows) {
      Vue.$log.debug('Enabling shadow map')
      context.renderer.shadowMap.enabled = true
      // renderer.shadowMap.type = Three.PCFSoftShadowMap // softer shadows
    }

    // remove default NGL lights; we'll add our own
    let light = context.scene.children.find((obj) => obj.type === 'SpotLight')
    if (light)
      context.scene.remove(light)
    light = context.scene.children.find((obj) => obj.type === 'AmbientLight')
    if (light)
      context.scene.remove(light)

    this.postprocessEffects.setParams(stage.viewer.width, stage.viewer.height, this.shot.postEffects.params)

    await this.loadComponents()

    if (this.shouldLoadBackgroundScene)
      await this.loadBackgroundScene(true, false)

    await this.loadCameraTrack()

    // could use `ticked` or `rendered` here; ticked is fired before
    // the render, and seems to give much smoother, more responsive animation.
    stage.viewer.signals.ticked.add( (args: TickArgs) => {
      this.onTick(args)
    })

    // Set the initial scene transforms before adding the changed
    // callback
    this.setSceneTransformFromParams()
    stage.viewerControls.signals.changed.add(this.onViewerControlsChanged)

    setTimeout(() => {
      // only one of these will actually fire
      maybeStartTour(document.querySelector('#app') || document.body, 'intro')
      maybeStartTour(document.querySelector('#app') || document.body, 'newFeatures')
    }, 500)
  }

  get followCameraTrackMode() {
    return userStore.profile?.ui.shotDesigner.followCameraTrackMode
  }

  @Watch('followCameraTrackMode')
  onCameraTrackChanged(active: boolean) {
    const viewer = this.context()?.viewer
    if (!viewer)
      return
    if (active)
      this.cameraRig?.setup(viewer)
    else
      this.cameraRigFree?.setup(viewer)
  }

  // Download a scene containing camera animation and import it to
  // keyframes
  async importCameraAnimation(cameraTrack: CameraTrack, setFollowMode = true) {
    if (cameraTrack.storagePath) {
      try {
        const r = await download(cameraTrack.storagePath)
        const data = readCameraFromScene(await r.text())
        // direct mutation to set up params (in case they're not in the
        // saved data)
        shotStore.addCameraParams(this.shot)
        shotStore.modifyParamStore({
          description: 'Import Camera Track',
          target: this.shot,
          path: ['camera'],
          mutator: (target: ParamStore) => {
            const camera = target.params as CameraParams
            camera.baseR.keyframeList = data.baseR || new KeyframeList(0)
            camera.baseTheta.keyframeList = data.baseTheta || new KeyframeList(0)
            // camera.baseX.keyframeList = data.baseX // NOTE: X/Y not yet fully implemented
            // camera.baseY.keyframeList = data.baseY
            camera.standHeight.keyframeList = data.standHeight
            camera.boomLength.keyframeList = data.boomLength
            camera.boomRotation.keyframeList = data.boomRotation
            camera.boomElevation.keyframeList = data.boomElevation
            camera.lookAt.keyframeList = data.lookAt
            camera.roll.keyframeList = data.roll
            // Camera tracks are defined as 10 sec long;
            // rescale to actual shot duration.
            target.rescaleTimes(10.0, this.shot.durationInSec)
          }
        })
        this.$toast('Camera animation imported')
        if (setFollowMode)
          userStore.setFollowCameraTrack(true)
        shotStore.requestSave({shot: this.shot, path: ['camera']})
      } catch (err) {
        console.error(`Importing camera anim ${cameraTrack?.id} "${cameraTrack?.name}": ${fullStack(err)}`)
        this.$toast.error('Error importing camera animation')
      }
    }
  }

  /** set up default camera animation param values */
  setupCameraAnimation() {
    if (!this.shot)
      return

    const camera = this.shot.camera.params
    if (camera.baseR.keyframeList.getNumKeyframes() > 0 ||
      camera.standHeight.keyframeList?.getNumKeyframes() > 0) // already set up
      return;

    // Use paramMutation directly, so this won't be undoable.
    // Don't worry about saving: if the keyframes don't get saved, we
    // can set them up again next time.
    // Both of these will probably change when we start importing
    // camera moves from presets.

    shotStore.paramMutation(
      {target: this.shot.camera,
       param: '',
       mutator: (target: ParamStore, param) => {
         camera.baseR.keyframeList.add(new Keyframe(0, 50))
         camera.baseTheta.keyframeList.add(new Keyframe(0, 180))
         camera.standHeight.keyframeList.add(new Keyframe(0, 15))
         camera.boomLength.keyframeList.add(new Keyframe(0, 0))
         camera.boomRotation.keyframeList.add(new Keyframe(0, 0))
         camera.boomElevation.keyframeList.add(new Keyframe(0, 0))
         camera.lookAt.keyframeList.add(new Keyframe(0, [0, 1, 0]))
         camera.roll.keyframeList.add(new Keyframe(0, 0))
       }})

  }

  /** Set the overall scene positionGroup & rotationGroup from shot.transform params.
        This is done per frame to animate the position & rotation. */
  private setSceneTransformFromParams() {
    const viewer = this.context()?.viewer
    if (!this.shot || !viewer)
      return false

    const oldPosition = tmpV3.copy(viewer.translationGroup.position)
    const oldRotation = tmpQuat.copy(viewer.rotationGroup.quaternion)
    const transform = this.shot.transform.params
    viewer.translationGroup.position.fromArray(transform.position.value)
    viewer.rotationGroup.quaternion.fromArray(transform.rotation.value)
    if (!fuzzyEqual(viewer.translationGroup.position, oldPosition) ||
        !fuzzyEqual(viewer.rotationGroup.quaternion, oldRotation))
      return true
    else
      return false
  }

  private sceneTransformToString() {
    const viewer = this.context()?.viewer
    if (!viewer)
      return '(no viewer)'
    const viewerRotation = viewer.rotationGroup.quaternion
    const transformRotation = this.shot.transform.params.rotation.value
    return `  viewer rot: ${v4ToString(viewerRotation)}\n  transform rot: ${v4ToString(transformRotation)}`
  }

  /** Set the current value of all animatable representation params */
  private setModelParams() {
    const t0 = Date.now()
    let nParamsSet = 0
    for (const comp of this.shot.components) {
      const nglComp = this.getNGLComponent(comp.id)
      if (!nglComp)
        continue
      for (const repr of comp.representations) {
        const nglRepr = this.getNGLRepresentation(comp.id, repr)
        if (!nglRepr)
          continue
        const reprParamStore = comp.representationParams[repr.id]
        const paramNames = Object.keys(reprParamStore.params)
        const nReprParamsSet = ParamsUtils.setRepresentationParams(reprParamStore.params,
                                                                   comp.componentParams.params,
                                                                   nglRepr, paramNames)
        nParamsSet += nReprParamsSet
      }
    }
    const t1 = Date.now()
    if (t1 - t0 > 10)
      console.log(`setModelParams: took ${t1 - t0} msec for ${nParamsSet} params`)
    return nParamsSet > 0
  }

  private setCameraZoom() {
    const zoomParamStore = this.shot.cameraZoom
    const zoom = zoomParamStore.params.zoom.value
    const viewer = this.context()?.viewer
    const camera = this.context()?.viewer?.camera as Three.PerspectiveCamera || undefined
    if (!camera)
      return
    if (camera.zoom != zoom) {
      camera.zoom = zoom
      return true               // something changed
    }
    return false
  }

  static lastTickTime = 0      // msec
  private onTick(args: TickArgs) {
    // args: currentTime, startTime, count, frames, lastDuration, lastFps, etc.
    // It seems args.lastDuration is not reliable, so we keep our own timer.
    if (!this.shot)
      return

    const viewer = this.context()?.viewer
    if (!viewer)
      return

    const now = window.performance.now() // msec
    const deltaT = now - NGL.lastTickTime

    const sceneTransformChanges = this.setSceneTransformFromParams()
    const modelParamChanges = this.setModelParams()

    const cameraZoomChanges = this.setCameraZoom()

    const postprocessEffectChanges = this.postprocessEffects ?
      this.postprocessEffects.setParams(viewer.width,
                                        viewer.height,
                                        this.shot.postEffects.params) : false

    let rigChanges = false

    if (this.followCameraTrackMode) {
      // Set camera keyframes if not already set
      this.setupCameraAnimation()

      const camera = this.shot.camera.params

      const pose = getCameraPose({
        baseR: camera.baseR.keyframeList,
        baseTheta: camera.baseTheta.keyframeList,
        standHeight: camera.standHeight.keyframeList,
        boomLength: camera.boomLength.keyframeList,
        boomRotation: camera.boomRotation.keyframeList,
        boomElevation: camera.boomElevation.keyframeList,
        roll: camera.roll.keyframeList || new KeyframeList(0),
        lookAt: camera.lookAt.keyframeList || new KeyframeList([0, 0, 0]),
      }, projectStore.localTime)

      rigChanges =
          this.cameraRig?.updateCameraRig({baseX: pose.baseX,
                                           baseY: pose.baseY,
                                           standHeight: pose.standHeight,
                                           boomLength: pose.boomLength,
                                           boomRotation: pose.boomRotation,
                                           boomElevation: pose.boomElevation,
                                           cameraRoll: pose.roll,
                                           cameraLookAt: tmpV3.set(pose.lookAt[0], pose.lookAt[1], pose.lookAt[2])}) || false
      // console.log(`onTick(${args.currentTime.toFixed(2)}): rig=${this.cameraRig?.cameraRigToStr()}`)
    } else {
      const t = projectStore.localTime
      // const theta = t / 10 * degToRad(360)
      // const phi = t / 10 * degToRad(89.5)
      // const dolly = 30
      if (this.freeCameraMouseControls) {
        const cameraParams = this.freeCameraMouseControls.onTick(deltaT / 1000)
        rigChanges =
            this.cameraRigFree?.updateCameraRig(cameraParams) || false
        // console.log(`onTick(${args.lastDuration.toFixed(2)}, t=${projectStore.localTime.toFixed(2)}): rig=${this.cameraRigFree?.cameraRigToStr()}`)
      }
    }

    // Only request a render if things changed. Otherwise NGL never does SSAA still rendering.
    const anyChanges = sceneTransformChanges || modelParamChanges || cameraZoomChanges ||
        postprocessEffectChanges || rigChanges
    if (anyChanges) {
      // console.log(`Requesting render: sceneTransform=${sceneTransformChanges}, model=${modelParamChanges}, ` +
      //   `postproc=${postprocessEffectChanges}, rig=${rigChanges}`)
      viewer.requestRender()
    }
    NGL.lastTickTime = now
  }

  // Called when rotationGroup/translationGroup transforms change
  private onViewerControlsChanged() {
    const viewer = this.context()?.viewer
    if (!this.shot || !viewer)
      return

    // Note: significant overlap with matrixChanged callback in
    // onComponentLoaded. Could make a helper function.
    const store = this.shot.transform
    const position = viewer.translationGroup.position
    const oldPosition = new Three.Vector3().fromArray(store.params.position.value)
    if (!fuzzyEqual(position, oldPosition))
      store.setParam({name: 'position', value: position.toArray()})

    const rotation = viewer.rotationGroup.quaternion
    const oldRotation = new Three.Quaternion().fromArray(store.params.rotation.value)
    if (!fuzzyEqual(rotation, oldRotation))
      store.setParam({name: 'rotation', value: rotation.toArray()})
  }

  private async loadBackgroundScene(addAxes = true, addLights = true) {
    this.showBgLoadingProgress = true
    this.progressPercent = 0.01
    this.message = 'Loading background scene, please wait ...'

    try {
      const stage = this.context()?.stage
      const scene = this.context()?.scene
      const viewer = this.context()?.viewer
      if (stage && scene && viewer) {
        // console.log('before loadBackgroundScene, NGL scene:')
        // this.dumpScene(scene, 6)
        // load into scene
        const parent = scene
        if (parent) {
          const bgScene = parent.getObjectByName('imported bg scene')
          if (bgScene)
            bgScene.parent?.remove(bgScene)
          const bgSceneScaled = parent.getObjectByName('imported bg scene scaled')
          if (bgSceneScaled)
            bgSceneScaled.parent?.remove(bgSceneScaled)

          const sceneTemplate = await templateStore.loadBgSceneTemplate(this.shot)
          if (sceneTemplate) {
            // console.log(`Scene template: ${inspect(sceneTemplate)}`)
            const backgroundGroup = parent.getObjectByName('bg scene')
            if (backgroundGroup) {
              await this.importSceneFile(sceneTemplate.id, 'imported bg scene',
                                         sceneTemplate.storagePath,
                                         sceneTemplate.storagePathDir,
                                         backgroundGroup)
            }
            if (sceneTemplate.scaledStoragePath) {
              const scaledBackgroundGroup = parent.getObjectByName('scaled bg scene')
              if (scaledBackgroundGroup) {
                await this.importSceneFile(sceneTemplate.id + '-scaled', 'imported bg scene scaled',
                                           sceneTemplate.scaledStoragePath,
                                           sceneTemplate.storagePathDir,
                                           scaledBackgroundGroup)
              }
            }
          }
          viewer.updateBoundingBox()
          if (addLights)
            this.addLights(parent)
            // add axes to top-level scene, for orientation
          if (addAxes)
            this.addAxes(parent)
          this.setAxesVisible(scene, this.axesVisible)
          // 10x10 XZ grid, 10 divisions (so small boxes are 1x1)
          parent.add(new Three.GridHelper(10, 10))
        }
        console.log('after loadBackgroundScene, NGL scene:')
        this.dumpScene(scene, 6)
        // done; clear loading UI
        this.message = ''
        this.showBgLoadingProgress = false
        viewer.render()
      }
    } catch (error) {
      console.log(`Error loading background scene: ${error.toString()}`)
      console.log(`Stack trace: ${error.stack}`)
      this.showBgLoadingProgress = false
      this.message = 'Error loading bg scene'
    }
  }

  private async loadComponents() {
    const stage = this.context()?.stage
    if (stage) {
      try {
        this.loadingComponents = true
        stage.removeAllComponents() // only removes NGL objects, not the set

        // ensure there is a valid current component in the shot
        if ((!this.shot.currentComponent || this.shot.currentComponent == '')) {
          if (this.shot.components.length > 0) {
            this.shot.setCurrentComponent(this.shot.components[0].id)
          } else {
            this.shot.setCurrentComponent()
            return                // no components; nothing to do
          }
        }

        // load components' models and representations
        for (const comp of this.shot.components)
          await this.loadModel(comp.id, comp.molecule, comp.representations)

        for (const comp of this.shot.components) {
          const obj = this.getNGLComponent(comp.id)
          if (obj) {
            this.autoscaleComponent(comp.id, obj)
            // bind signal handler's `this` to the component, and
            // use higher priority to run before matrix update.
            if (obj instanceof NGLViewer.StructureComponent)
              obj.selection.signals.stringChanged.add(onCompSelectionChanged, obj, 1)

            this.onComponentLoaded(comp.id, obj)
          }
        }
        this.loadingComponents = false

        // Set up the default transform, then apply it to the scene
        this.setSceneTransformFromParams()

        if (this.shot.currentComponent) {
          let obj = this.getNGLComponent(this.shot.currentComponent)
          if (!obj)
            obj = this.getNGLComponent(this.shot.components[0].id) // try first one
          if (obj) {
            this.selectComponent(obj)
            stage.animationControls.zoom(obj.getZoom(), 1000)
          }
        }
      } finally {
        this.loadingComponents = false // just in case
      }
    }

    // maybe show component/representation tour
    // (this is as good a place to do it as anywhere)
    // Only if we already showed the intro tour though.
    if (this.shot.components.length > 0 && tourShownStatus('intro')) {
      setTimeout(() => {
        maybeStartTour(document.querySelector('#app') || document.body, 'molParams')
      }, 500)
    }
    setTimeout(() => {
      // After a couple of minutes of usage, show the render tour
      maybeStartTour(document.querySelector('#app') || document.body, 'render')
    }, 2*60*1000)
    setTimeout(() => {
      // After longer usage, ask for feedback
      maybeStartTour(document.querySelector('#app') || document.body, 'feedback')
    }, 5*60*1000)
  }

  /** load a model (component) and its representations.
     Should only be called from loadComponents()
   */
  private async loadModel(componentId: string, path: string|File|Blob,
                          representations: RepresentationSet) {
    const stage = this.context()?.stage
    const scene = this.context()?.scene
    if (!stage || !scene)
      return
    let obj = null              // obj is a StructureComponent
    try {
      obj = await stage.loadFile(path, {defaultRepresentation: false})
    } catch (error) {
      Vue.$log.warn('Error loading', path, 'error:', error)
      // Vue.$toast.error(`Error loading ${name}: ${error}`)
    }
    if (!obj) {
      Vue.$log.warn('File ', path, 'did not load a model')
      return
    }
    if (obj instanceof NGLViewer.StructureComponent) {
      this.loadRepresentations(componentId, obj,
                               representations as RepresentationSet<NGLViewer.StructureRepresentationType>)
    } else if (obj instanceof NGLViewer.VolumeComponent) {
      this.loadRepresentations(componentId, obj,
                               representations as RepresentationSet<NGLViewer.VolumeRepresentationType>)
    } else {
      Vue.$log.warn('obj type is not StructureComponent: implement it', obj)
    }

    const lastShotComponent = this.shot.components[stage.compList.length - 1]

    // Set up list of Assemblies (symmetries)
    if (obj.type === 'structure') {
      const comp = (obj as NGLViewer.StructureComponent)
      const biomolDict: { [name: string]: NGLViewer.Assembly } = comp.structure.biomolDict
      const defaultAssembly = comp.structure.unitcell ? 'AU' : 'FULL'

      this.shot.setComponentUuid(componentId, comp.uuid)

      const assemblies = Object.keys(biomolDict)
      assemblies.unshift(defaultAssembly)
      const compInfo = this.componentInfo(componentId)
      if (compInfo) {
        compInfo.componentParams.setDefaultValue({name: 'assembly', value: defaultAssembly})
        this.setAssemblyOptions(compInfo.componentParams, assemblies)
      }
      comp.setDefaultAssembly(this.assemblies[componentId])

      // Set ngl structure name to shot component id
      // NOTE: this fixes names of structures loaded from GCS
      if (lastShotComponent && lastShotComponent.name)
        comp.structure.name = lastShotComponent.name
    } else if (obj.type === 'volume') {
      const comp = (obj as NGLViewer.VolumeComponent)
      this.shot.setComponentUuid(componentId, comp.uuid)
      console.log(`Loaded volume ${componentId}, NGL UUID ${comp.uuid}`)
      // XXX handle changed name from lastShotComponent
    }

    // If the representations include some complicated ones, like marching-cubes
    // surfaces, those will be created as background tasks.
    // Vue.$log.info('After load model, tasks = ', stage.tasks)
    // This is how to wait for the representation's graphics objects to be fully loaded
    this.showBgTaskProgressIndicator = true
    this.showBgTaskProgressText = 'Loading model...'
    stage.tasks.onZeroOnce(() => {
      Vue.$log.info('Model fully loaded')
      this.showBgTaskProgressIndicator = false
    })

    // enable shadows for the model
    const top = scene.getObjectByName('modelGroup')
    if (top) {
      // Vue.$log.debug('Enabling shadows for model', name)
      // enableShadows(top)
    }

    // console.log(`Molecule scene at end of loadModel:`)
    // this.dumpScene(scene)
  }

  // loadRepresentations can either take a structure and some structure representations,
  // _or_ a volume and some volume representations.
  loadRepresentations(componentId: string,
    obj: NGLViewer.StructureComponent,
    representations: RepresentationSet<NGLViewer.StructureRepresentationType>): void
  loadRepresentations(componentId: string,
    obj: NGLViewer.VolumeComponent,
    representations: RepresentationSet<NGLViewer.VolumeRepresentationType>): void
  loadRepresentations(componentId: string,
                      obj: NGLViewer.StructureComponent | NGLViewer.VolumeComponent,
                      representations: RepresentationSet) {
    representations.forEach((elt, idx) => {
      Vue.$log.debug(`Adding representation ${idx}:`, elt.id, elt.kind)
      let r
      if (obj instanceof NGLViewer.StructureComponent) {
        r = obj.addRepresentation(elt.kind as NGLViewer.StructureRepresentationType,
                                  {side: 'front'})  // default options
      } else {
        r = obj.addRepresentation(elt.kind as NGLViewer.VolumeRepresentationType,
                                  {side: 'front'}) // default options
      }
      elt.uuid = r.uuid
      this.onRepresentationAdded(componentId, elt.id, r)
    })
  }

  // for use in mounted()
  async loadCameraTrack() {
    if (this.shot.cameraTrackId) {
      await cameraTrackStore.loadCameraTracks()
      const cameraTrack = cameraTrackStore.cameraTracks.find((ct: CameraTrack) => ct.id === this.shot.cameraTrackId)
      if (cameraTrack) {
        await this.importCameraAnimation(cameraTrack, false)
        this.setupCameraAnimation()
      }
    }
  }

  selectComponent(nglComp: NGLViewer.Component) {
    // Get component ID for this NGL comp, then make it current
    const id = this.getComponentId(nglComp.uuid)
    this.shot.setCurrentComponent(id)
    const stage = this.context()?.stage
    if (stage)
      stage.transformComponent = nglComp
  }

  get currentComponentId(): string|null {
    return this.shot.currentComponent
  }

  set currentComponentId(id: string|null) {
    this.shot.setCurrentComponent(id)
    if (id) {
      const nglComp = this.getNGLComponent(id)
      const stage = this.context()?.stage
      if (nglComp && stage)
        stage.transformComponent = nglComp
    }
  }

  keyboardState: {ctrl: boolean; shift: boolean} = {
    ctrl: false,
    shift: false,
  }

  componentInfo(id: string) {
    return this.shot.components.find(c => c.id === id)
  }

  get allRepresentations(): ComponentReprs {
    return this.shot.components.reduce((map, c) => {
      map[c.id] = c.representations
      return map
    }, {} as ComponentReprs)
  }

  get assemblies(): { [compId: string]: string } {
    return this.shot.components.reduce((map, c) => {
      map[c.id] = c.componentParams.params.assembly.value
      return map
    }, {} as {[compId: string]: string})
  }

  get compSelections(): { [compId: string]: string } {
    return this.shot.components.reduce((map, c) => {
      map[c.id] = c.componentParams.params.selection.value
      return map
    }, {} as {[compId: string]: string})
  }

  dump() {
    const scene = this.context()?.scene
    if (scene)
      this.dumpScene(scene, 6, true)
  }

  dumpScene(scene: Three.Object3D, // a Three.Scene or any Three.Object3D
            maxLevel?: number,
            includeMaterials?: boolean) {
    const dump1 = (obj: Three.Object3D, level: number, prefix: string) => {
      if (level > (maxLevel ? maxLevel : 4))
        return
      if (obj === undefined) {
        console.log(`${prefix}${level}: undefined`)
        return
      }
      const msg = `${prefix}${level}: ${obj.type} "${obj.name}" (${obj.children.length} children)`
      if (obj.children.length && level > 3)
        console.groupCollapsed(msg)
      else if (obj.children.length)
        console.group(msg)
      else
        console.log(msg)
      if (obj.type.includes('ight'))
        console.log(`${prefix}${level}   at`, obj.position)
      if (includeMaterials) {
        // make Typescript happy:
        // ob is an Object3D that may have a material property
        const ob: Three.Object3D & {material?: any} = obj
        if ('material' in ob && ob.type != 'LineSegments')
          console.log(`${prefix} : material`, ob.material)
      }
      obj.children.forEach((child: Three.Object3D) => {
        dump1(child, level + 1, prefix + '  ')
      })
      if (obj.children.length)
        console.groupEnd()
    }
    dump1(scene, 0, '')
  }

  removeRepresentation(index: number) {
    if (this.currentComponentId)
      this.shot.removeRepresentation(this.currentComponentId, index)
  }

  /** Set the NGL component's transform - t is the desired user transform
   Note that the actual comp matrix also has its centering translation.
   Final matrix M = (T:pos+ctr) * (S:scale) * (R:quat) * (T:-ctr)
  */
  setComponentTransform(componentId: string, t: Transform) {
    const comp = this.getNGLComponent(componentId)
    if (!comp)
      return
    if (this.moleculeAutoScaleVerbose)
      Vue.$log.debug(`Setting component transform for ${componentId.slice(0,7)} to pos=${v3ToString(t.position)}, scale=${scaleToString(t.scale)}`)
    comp.position = t.position
    comp.scale = t.scale
    comp.quaternion = t.rotation
    comp.updateMatrix()
    if (this.moleculeAutoScaleVerbose)
      Vue.$log.debug(`  comp matrix =\n${matrix4ToString(comp.matrix, '', 4)}`)
  }

  /** "autoscale": set the scale factor for a component, so it's reasonably sized. */
  autoscaleComponent(componentId: string,
                     obj: NGLViewer.StructureComponent | NGLViewer.VolumeComponent) {
    if (!this.context()?.stage)
      return
    const componentIndex = this.shot.components.findIndex(c => c.id === componentId)
    // For the default (first) component, normalize the size, otherwise use
    // the same scale as the first component
    if (componentIndex == 0) {
      const bbox = getComponentVisualBbox(obj)
      if (this.moleculeAutoScaleVerbose)
        Vue.$log.info(`getting bbox: ${inspect(bbox.clone())}`)

      const standardSize = 2.0 // 2 units in longest dimension
      let scale = 1

      const size = new Three.Vector3()
      bbox.getSize(size)
      scale = standardSize / Math.max(size.x, size.y, size.z)

      if (Math.abs(scale) < 0.0001)
        Vue.$log.warn(`Component ${componentId} (index ${componentIndex}): setting scale to zero!`)

      if (this.moleculeAutoScaleVerbose)
        Vue.$log.info(`Setting component ${componentIndex} scale to ${scale}`)

      if (componentIndex == 0)
        this.setGlobalScale(scale)

    }
  }

  setGlobalScale(scaleFactor: number) {
    const viewer = this.context()?.viewer
    if (!viewer)
      return
    Vue.$log.info(`Setting object-scene scale factor to ${scaleFactor} (in rotationGroup and scaled bg scene)`)
    viewer.rotationGroup.scale.set(scaleFactor, scaleFactor, scaleFactor)
    const scaledBackgroundGroup = viewer.scene.getObjectByName('scaled bg scene')
    if (scaledBackgroundGroup)
      scaledBackgroundGroup.scale.set(scaleFactor, scaleFactor, scaleFactor)

  }

  onComponentLoaded(componentId: string,
                    obj: NGLViewer.StructureComponent | NGLViewer.VolumeComponent) {
    this.setComponentTransform(componentId, this.allComponentTransforms[componentId])

    obj.signals.matrixChanged.add((m) => {
      if (!this.shot)
        return

      if (this.moleculeAutoScaleVerbose)
        Vue.$log.info(`component ${componentId.slice(0, 7)} matrix changed`)
      // The matrix can change either because the param values in the
      // shot changed (e.g. because of undo or moving to a different
      // time), or because the user changed them through direct
      // manipulation.
      // In the first case, the component values will already match
      // the shot params, and we want to ignore the change to avoid
      // creating excess keyframes, etc.
      // In the second case, the value will not match and we need to
      // update the params.
      // We also have to account for the autoscaling and centering;
      // those are in the component's matrix but we don't want them
      // included in our params.
      let changedParams = false
      const comp = this.shot.components.find(c => c.id === componentId)
      const store = comp?.transform
      if (!comp || !store)
        return

      const tmpV = new Three.Vector3()
      // note: this has to match allComponentTransforms()
      const center = getComponentRawCenter(obj)
      const visualCenterToOrigin = componentVisualCenterToOrigin(obj)
      const oldPosition = new Three.Vector3().fromArray(store.params.position.value)
      if (this.moleculeAutoScaleVerbose) {
        Vue.$log.info(`matrix changed: checking comp position\n` +
                      `obj.pos=${v3ToString(obj.position)}\n center=${v3ToString(center)}\nold pos=${v3ToString(oldPosition)}`)
      }

      // if obj.position + center != oldPosition, set position = obj.position + center
      tmpV.addVectors(obj.position, visualCenterToOrigin)
      if (!fuzzyEqual(tmpV, oldPosition)) {
        changedParams = true
        store.setParam({name: 'position', value: tmpV.toArray()})
      }

      const rotation = obj.quaternion
      const oldRotation = new Three.Quaternion().fromArray(store.params.rotation.value)
      if (!fuzzyEqual(rotation, oldRotation)) {
        changedParams = true
        store.setParam({name: 'rotation', value: rotation.toArray()})
      }

      // When a component is dragged, make it current
      if (changedParams)
        this.currentComponentId = componentId

      this.updateDebugText++    // to force debug text update
    })
  }

  /** Get all components' transforms from their params' current values.
      Includes comp's center & scale factors. */
  // This has to match the checking code in the matrixChanged callback
  get allComponentTransforms(): ComponentTransforms {
    if (!this.shot)
      return {}
    const transforms = this.shot.components.reduce((map, c) => {
      const params = c.transform.params
      const comp = this.getNGLComponent(c.id)
      let center, visualCenterToOrigin
      if (comp) {
        center = getComponentRawCenter(comp)
        visualCenterToOrigin = componentVisualCenterToOrigin(comp)
      } else {
        center = new Three.Vector3()
        visualCenterToOrigin = center
      }

      map[c.id] = {position: new Three.Vector3().fromArray(params.position.value).sub(visualCenterToOrigin),
                   scale: new Three.Vector3().fromArray(params.scale.value),
                   rotation: new Three.Quaternion().fromArray(params.rotation.value)}
      return map
    }, {} as ComponentTransforms)
    return transforms
  }

  @Watch('allComponentTransforms')
  onTransformChanged(newval: ComponentTransforms, oldval: ComponentTransforms) {
    for (const [id, transform] of Object.entries(newval)) {
      if (!fuzzyEqual(transform, oldval[id]))
        this.setComponentTransform(id, transform)
    }
  }

  @Watch('spinning')
  onSpinning(spinning: boolean, oldVal: boolean) {
    this.context()?.stage?.setSpin(spinning)
  }

  @Watch('assemblies')
  onAssemblyChange(newval: { [compId: string]: string }) {
    forEachEntry(newval, (id, val) => {
      const obj = this.getNGLComponent(id)
      if (obj) {
        if (obj instanceof NGLViewer.StructureComponent)
          obj.setDefaultAssembly(val)
        this.autoscaleComponent(id, obj)
      }
    })
  }

  // When the selection param changes, set that into NGL's component selection.
  @Watch('compSelections')
  onCompSelectionParamChange(newval: { [compId: string]: string }) {
    if (this.loadingComponents)
      return // don't try to do this while still loading; it'll cause problems in ngl
    forEachEntry(newval, (id, val) => {
      const obj = this.getNGLComponent(id)
      if (obj && obj instanceof NGLViewer.StructureComponent)
        obj.setSelection(val)   // will trigger our selection signal handler
      // note: can get selection error from obj.selection?.selection?.error
    })
  }

  @Watch('shouldLoadBackgroundScene')
  async onShouldLoadBackgroundScene(newval: boolean, oldval: boolean) {
    if (newval === oldval) return
    if (!newval) {
      if (this.context()?.scene && this.context()?.viewer) {
        const obj = this.context()?.scene.getObjectByName('Background Scene Group')
        if (obj && obj.parent) {
          const parent = obj.parent
          console.log(`Removing ${obj.name}, parent=${parent.name}`)
          // XXX: should dispose() obj's geometry & material; need to recurse?
          // Have to remove objs from their direct parent
          // XXX: this isn't removing the lights, need to fix that.
          parent.remove(obj)
          this.context()?.viewer.render()
        }
      }
    } else {
      await this.loadBackgroundScene(true, false)
    }
  }

  // Get all molecule names. This only exists to connect to the
  // watcher below, so we can update NGL components.
  // Note: returns a string so Vue's watch code, which compares using `===`,
  // will compare correctly. If we returned an array of strings, simple `===`
  // would return false differences (e.g.: ['a' === 'a'] is false)
  get molecules() {
    return this.shot.components.map(c => c.molecule).join(',')
  }
  @Watch('molecules')
  async onComponents(newVal: string[], oldVal: string[]) {
    console.log(`NGL components changed ${oldVal} -> ${newVal}; reloading`)
    this.clearRepresentations()
    await this.loadComponents()
  }

  clearRepresentations() {
    // Cancel all watchers
    this.representationWatchers.map(w => w())
    this.representationWatchers = []
    // Discard the representationParamStore to trigger rebuilding of
    // all params and watchers
    this.representationParamStore = {}
  }

  @Watch('allRepresentations')
  async onRepresentations(newVal: ComponentReprs, oldVal: ComponentReprs) {
    const newRep = newVal       // all reprs of all components
    // Note: NGL may not have all the components in newRep, because we
    // may still be loading them. So we ignore components that don't exist in NGL (yet).

    this.context()?.stage.eachComponent((c) => {
      const compId = this.getComponentId(c.uuid)
      if (!compId) {
        Vue.$log.error(`onRepresentations: found NGL comp ${c.uuid} without our comp`)
        return
      }
      const reprs = newRep[compId]
      // delete any NGL reprs not found in our list
      c.eachRepresentation(r => {
        const i = reprs.findIndex(repr => repr.uuid === r.uuid)
        if (i < 0)
          c.removeRepresentation(r)
      })
      // add any reprs in our list that aren't in NGL
      const reprsToAdd: RepresentationSet = []
      reprs.forEach(repr => {
        const i = c.reprList.findIndex(r => r.uuid === repr.uuid)
        if (i < 0)
          reprsToAdd.push(repr)
      })
      if (reprsToAdd.length) {
        if (c instanceof NGLViewer.StructureComponent) {
          this.loadRepresentations(compId, c,
                                   reprsToAdd as RepresentationSet<NGLViewer.StructureRepresentationType>)
        } else if (c instanceof NGLViewer.VolumeComponent) {
          this.loadRepresentations(compId, c,
                                   reprsToAdd as RepresentationSet<NGLViewer.VolumeRepresentationType>)
        }
      }
    })
  }

  onRepresentationAdded(componentId: string,
                        reprId: string,
                        element: NGLViewer.RepresentationElement): void {
    this.setRepresentationToPreviewQuality(element)

    if (!this.shot)
      return

    if (!(reprId in this.representationParamStore)) {
      const comp = this.shot.getComponent(componentId)
      // Create the params, fill in their values from saved data (if
      // any), then add them to the shot.
      const reprParams = this.createRepresentationParams(element, comp?.type || 'structure')
      this.shot.setReprParamValuesFromData(componentId, reprId, reprParams)
      const reprParamStore = this.shot.addParams(componentId, reprId, reprParams)

      // this.representationParamStore[reprId] = reprParamStore
      Vue.set(this.representationParamStore, reprId, reprParamStore)
    }
  }

  toggleParamsDrawer() {
    (this.$refs.nglParams as any).toggleDrawer()
  }

  handleStageResize() {
    // Handle stage viewport resizing
    this.context()?.stage?.handleResize()
  }

  @Watch('expanded')
  onExpandedChange() {
    Vue.nextTick(this.handleStageResize)
  }

  onKeyDown(ev: KeyboardEvent) {
    if (ev.key === 'Control')
      nglStore.ctrlKeyDown()
    else if (ev.key === 'Shift')
      nglStore.shiftKeyDown()
  }

  onKeyUp(ev: KeyboardEvent) {
    if (ev.key === 'Control')
      nglStore.ctrlKeyUp()
    else if (ev.key === 'Shift')
      nglStore.shiftKeyUp()
  }

  onMouseDown(ev: MouseEvent) {
    nglStore.mouseDown(ev.button)
  }

  onMouseUp(ev: MouseEvent) {
    nglStore.mouseUp(ev.button)
  }

  setAssemblyOptions(store: ParamStore, options: string[]) {
    const param = store.params.assembly as Param<ChoiceParamDef, string>
    const def = param.definition as ChoiceParamDef
    const choices = options.map(val => ({text: val, value: val}))
    let setChoices = false
    let valueToSet = param.value

    if (def.choices.length == 0)
      setChoices = true
    if (!options.includes(param.value))
      valueToSet = param.defaultValue

    if (setChoices || valueToSet != param.value) {
      shotStore.paramMutation({
        target: store,
        param: 'assembly',
        mutator: (target, _param) => {
          const p = target.params.assembly as Param<ChoiceParamDef, string>
          p.definition.choices = choices
          p.setValue(valueToSet)
        }
      })
    }
  }

  // Enable shadows recursively for object and all its visible descendants
  // XXX: not working -- makes some representations not show up at all.
  enableShadowForLight(light: Three.Light) {
    light.castShadow = true
    light.shadow.mapSize.width = 512
    light.shadow.mapSize.height = 512
    if (light.shadow.camera instanceof Three.PerspectiveCamera ||
      light.shadow.camera instanceof Three.OrthographicCamera) {
      light.shadow.camera.near = 10
      light.shadow.camera.far = 5000
    }
    const d = 300
    if (light.shadow.camera instanceof Three.OrthographicCamera) {
      light.shadow.camera.left = -d
      light.shadow.camera.right = d
      light.shadow.camera.top = d
      light.shadow.camera.bottom = -d
    }
  }

  addLights(scene: Three.Object3D) {
    // Scene size with bg seems to be around 2000x2000
    // X is left, Y is up, Z is toward the back of the set
    // (turn on axes/grid to get oriented)
    const lightIntensity = 1.3
    const warmColor = 0xfffdd9
    const warmLight = new Three.DirectionalLight(warmColor, lightIntensity)
    warmLight.name = 'warmLight'
    warmLight.position.set(50, 50, -50)
    this.enableShadowForLight(warmLight)
    scene.add(warmLight)
    // scene.add(new Three.CameraHelper(warmLight.shadow.camera))

    const coolColor = 0xd9f0ff
    const coolLight = new Three.DirectionalLight(coolColor, lightIntensity * 0.5)
    coolLight.name = 'coolLight'
    coolLight.position.set(-50, 50, 50)
    this.enableShadowForLight(coolLight)
    scene.add(coolLight)

    const keyColor = 0xffcccc
    const keyLight = new Three.SpotLight(keyColor, lightIntensity * 0.5, 0,Math.PI/3, 0.5, 2)
    keyLight.name = 'keyLight'
    keyLight.position.set(0, -100, 400)
    this.enableShadowForLight(keyLight)
    const keyTarget = new Three.Object3D()
    keyTarget.name = 'keyLight target'
    keyTarget.position.set(0, 0, 0)
    scene.add(keyTarget)
    keyLight.target = keyTarget
    scene.add(keyLight)

    if (false) {
      const color = 0xffffff
      const intensity = 1.0
      const distance = 0 // unlimited
      const angle = Math.PI / 3
      const penumbra = 0.5 // 0.5 = normal, max=1
      const decay = 2 // physically realistic
      const underLight = new Three.SpotLight(color, intensity, distance, angle, penumbra, decay)
      underLight.name = 'underLight'
      underLight.position.set(0, -20, 20)
      const lightTarget = new Three.Object3D()
      lightTarget.name = 'underLight target'
      lightTarget.position.set(0, -20, 200)
      scene.add(lightTarget)
      underLight.target = lightTarget
      scene.add(underLight)
    }

    const hemiLight = new Three.HemisphereLight(0xffffff, 0xffffff, 0.25);
    hemiLight.color.setHSL(0.6, 1, 0.7);
    hemiLight.groundColor.setHSL(0.095, 0.5, 0.3);
    hemiLight.position.set(0, 50, 0);
    scene.add(hemiLight);
  }

  addAxes(scene: Three.Object3D) {
    const arrow = new Axis3DHelper(new Three.Vector3(0, 0, 0), 2, 0.003, 0.04, 0.007)
    arrow.userData.DSS = {exportIgnore: true}
    arrow.name = 'axes'
    scene.add(arrow)
  }

  setAxesVisible(scene: Three.Object3D, visible: boolean) {
    const axes = scene.getObjectByName('axes')
    if (!axes)
      return
    axes.visible = visible
  }

  get axesVisible() {
    return userStore.profile?.ui.shotDesigner.axesVisible || false
  }

  @Watch('axesVisible', {immediate: true})
  onAxesVisible(visible: boolean) {
    const scene = this.context()?.scene
    if (!scene)
      return
    this.setAxesVisible(scene, visible)
  }

  async reloadSceneTemplate(template: SceneTemplate) {
    await shotStore.changeSceneShotTemplate({shot: this.shot, selected: template})
    await this.loadBackgroundScene(false, false)
  }

  /** Save scene & glb files locally in browser, for testing */
  async doLocalSave(event?: {saveLocally: boolean}) {
    await this.doSave(event?.saveLocally ?? false )
  }

  undo() {
    if (undoStack.canUndo)
      undoStack.undo()
  }

  redo() {
    if (undoStack.canRedo)
      undoStack.redo()
  }

  deactivated() {
    nglStore.resetKeyboard()
    nglStore.resetMouse()
  }
}

